// ─── Multi-Tenant SaaS Types ──────────────────────────────────────────────────

export type TenantPlan = 'starter' | 'pro' | 'enterprise';
export type TenantStatus = 'provisioning' | 'active' | 'suspended' | 'cancelled';
export type BillingEventType = 'subscription_created' | 'subscription_renewed' | 'subscription_upgraded' | 'subscription_downgraded' | 'payment_failed' | 'subscription_cancelled' | 'subscription_updated' | 'plan_changed' | 'trial_started' | 'trial_ended';
export type ProvisioningStatus = 'pending' | 'creating_schema' | 'configuring_twilio' | 'configuring_cal' | 'sending_welcome' | 'completed' | 'failed';
export type AuditAction = 'tenant_created' | 'tenant_suspended' | 'tenant_reactivated' | 'tenant_deleted' | 'plan_changed' | 'impersonation_started' | 'impersonation_ended' | 'announcement_sent' | 'twilio_assigned' | 'cal_workspace_created';

export interface TenantPlanConfig {
  id: TenantPlan;
  name: string;
  price: number; // monthly EUR
  stripePriceId: string;
  maxOperators: number;
  maxCallsPerMonth: number;
  features: string[];
  color: string;
  popular?: boolean;
}

export const TENANT_PLANS: TenantPlanConfig[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    stripePriceId: process.env.STRIPE_PRICE_STARTER || 'price_starter',
    maxOperators: 2,
    maxCallsPerMonth: 500,
    features: [
      'Dashboard base',
      'Gestione pazienti',
      'Follow-up automatici',
      'Storico chiamate',
      '2 operatori',
      '500 chiamate/mese',
      'Supporto email',
    ],
    color: 'blue',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 149,
    stripePriceId: process.env.STRIPE_PRICE_PRO || 'price_pro',
    maxOperators: 10,
    maxCallsPerMonth: 5000,
    features: [
      'Tutto di Starter',
      'Analytics avanzate',
      'Integrazione Cal.com',
      'SMS & WhatsApp',
      'Supervisore dashboard',
      '10 operatori',
      '5.000 chiamate/mese',
      'Supporto prioritario',
    ],
    color: 'primary',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 399,
    stripePriceId: process.env.STRIPE_PRICE_ENTERPRISE || 'price_enterprise',
    maxOperators: -1, // unlimited
    maxCallsPerMonth: -1, // unlimited
    features: [
      'Tutto di Pro',
      'API accesso completo',
      'SLA garantito 99.9%',
      'Sub-account Twilio dedicato',
      'Workspace Cal.com dedicato',
      'Operatori illimitati',
      'Chiamate illimitate',
      'Supporto dedicato 24/7',
      'Onboarding personalizzato',
    ],
    color: 'accent',
  },
];

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone: string;
  vatNumber: string;
  address: string;
  city: string;
  plan: TenantPlan;
  status: TenantStatus;
  provisioningStatus: ProvisioningStatus;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  twilioSubAccountSid?: string;
  twilioPhoneNumber?: string;
  calWorkspaceId?: string;
  schemaName?: string;
  adminEmail: string;
  adminName: string;
  operatorCount: number;
  callsThisMonth: number;
  mrr: number;
  trialEndsAt?: string;
  currentPeriodEnd?: string;
  createdAt: string;
  updatedAt: string;
  lastActivityAt?: string;
}

export interface BillingEvent {
  id: string;
  tenantId: string;
  tenantName: string;
  type: BillingEventType;
  amount: number;
  currency: string;
  stripeEventId?: string;
  description: string;
  createdAt: string;
}

export interface AuditLogEntry {
  id: string;
  tenantId?: string;
  tenantName?: string;
  superAdminId?: string;
  superAdminEmail?: string;
  action: AuditAction;
  details: Record<string, unknown>;
  ipAddress?: string;
  createdAt: string;
}

export interface TwilioPoolNumber {
  id: string;
  phoneNumber: string;
  friendlyName: string;
  tenantId?: string;
  tenantName?: string;
  subAccountSid?: string;
  status: 'available' | 'assigned' | 'reserved';
  capabilities: { voice: boolean; sms: boolean; whatsapp: boolean };
  monthlyFee: number;
  assignedAt?: string;
  createdAt: string;
}

export interface SuperAdminStats {
  totalTenants: number;
  activeTenants: number;
  provisioningTenants: number;
  suspendedTenants: number;
  totalMrr: number;
  totalCallsThisMonth: number;
  totalOperators: number;
  planBreakdown: Record<TenantPlan, number>;
  recentSignups: number; // last 30 days
  churnedThisMonth: number;
}

export interface LabRegistrationForm {
  labName: string;
  vatNumber: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  plan: TenantPlan;
  stripePaymentMethodId?: string;
}
